import { FC, CSSProperties } from 'react';

export interface SkeletonProps {
  className?: string;
  variant?: 'text' | 'circular' | 'rectangular' | 'card' | 'table';
  count?: number;
  width?: string | number;
  height?: string | number;
}

export const LoadingSkeleton: FC<SkeletonProps> = ({
  className = '',
  variant = 'text',
  count = 1,
  width,
  height,
}) => {
  // Padrão oficial de tokens do Design System (ADR-0009): bg-surface-subtle border-surface-border
  const baseClasses = 'animate-pulse bg-surface-subtle rounded-md';

  const style: CSSProperties = {
    width:
      width !== undefined
        ? typeof width === 'number'
          ? `${width}px`
          : width
        : undefined,
    height:
      height !== undefined
        ? typeof height === 'number'
          ? `${height}px`
          : height
        : undefined,
  };

  if (variant === 'card') {
    return (
      <div
        className={`space-y-4 rounded-2xl border border-surface-border bg-surface-panel p-6 shadow-sm ${className}`}
      >
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 animate-pulse rounded-full bg-surface-subtle" />
          <div className="flex-1 space-y-2">
            <div className="h-4 w-1/3 animate-pulse rounded bg-surface-subtle" />
            <div className="h-3 w-1/4 animate-pulse rounded bg-surface-subtle" />
          </div>
        </div>
        <div className="bg-surface-subtle/70 h-20 animate-pulse rounded-xl" />
      </div>
    );
  }

  if (variant === 'table') {
    const rows = count || 4;
    return (
      <div
        className={`divide-surface-border/60 w-full divide-y rounded-2xl border border-surface-border bg-surface-panel p-4 shadow-sm ${className}`}
      >
        <div className="flex justify-between pb-3">
          <div className="h-4 w-1/4 animate-pulse rounded bg-surface-subtle" />
          <div className="h-4 w-1/6 animate-pulse rounded bg-surface-subtle" />
          <div className="h-4 w-1/6 animate-pulse rounded bg-surface-subtle" />
        </div>
        {Array.from({ length: rows }).map((_, idx) => (
          <div key={idx} className="flex justify-between py-3">
            <div className="h-3 w-1/3 animate-pulse rounded bg-surface-subtle" />
            <div className="h-3 w-1/6 animate-pulse rounded bg-surface-subtle" />
            <div className="h-3 w-16 animate-pulse rounded bg-surface-subtle" />
          </div>
        ))}
      </div>
    );
  }

  const getVariantClasses = () => {
    switch (variant) {
      case 'circular':
        return 'rounded-full';
      case 'rectangular':
        return 'rounded-xl';
      case 'text':
      default:
        return 'h-4 w-full rounded';
    }
  };

  if (count > 1) {
    return (
      <div className="space-y-2.5">
        {Array.from({ length: count }).map((_, idx) => (
          <div
            key={idx}
            style={style}
            className={`${baseClasses} ${getVariantClasses()} ${className}`}
          />
        ))}
      </div>
    );
  }

  return (
    <div
      style={style}
      className={`${baseClasses} ${getVariantClasses()} ${className}`}
    />
  );
};

/**
 * Shimmer estruturado para Grid de 4 Métricas (Topo das telas)
 */
export const MetricGridSkeleton: FC<{ count?: number }> = ({ count = 4 }) => {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4">
      {Array.from({ length: count }).map((_, idx) => (
        <div
          key={idx}
          className="flex items-center justify-between rounded-2xl border border-surface-border bg-surface-panel p-4 shadow-sm"
        >
          <div className="flex-1 space-y-2">
            <div className="h-3 w-24 animate-pulse rounded bg-surface-subtle" />
            <div className="h-6 w-16 animate-pulse rounded bg-surface-subtle" />
            <div className="h-2.5 w-32 animate-pulse rounded bg-surface-subtle" />
          </div>
          <div className="h-10 w-10 animate-pulse rounded-xl bg-surface-subtle" />
        </div>
      ))}
    </div>
  );
};

/**
 * Shimmer estruturado para o Card de Conexão do WhatsApp
 */
export const WhatsAppConnectionCardSkeleton: FC = () => {
  return (
    <div className="rounded-2xl border border-surface-border bg-surface-panel p-6 shadow-sm">
      <div className="flex flex-col items-center justify-between gap-6 sm:flex-row sm:items-start">
        <div className="flex flex-col items-center gap-4 text-center sm:flex-row sm:text-left">
          <div className="h-16 w-16 animate-pulse rounded-full bg-surface-subtle" />
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="h-5 w-40 animate-pulse rounded bg-surface-subtle" />
              <div className="h-4 w-12 animate-pulse rounded-full bg-surface-subtle" />
            </div>
            <div className="h-3.5 w-28 animate-pulse rounded bg-surface-subtle" />
            <div className="h-3 w-48 animate-pulse rounded bg-surface-subtle" />
          </div>
        </div>
        <div className="h-8 w-28 animate-pulse rounded-lg bg-surface-subtle" />
      </div>
    </div>
  );
};
